// src/components/Multiplayer/Tournament/TournamentMonitor.jsx
// Organizer/admin live monitor: tables + winners + eliminations. Opens in a separate window.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { tournamentService } from '../../../services/tournamentService';

const STATUS_LABEL = {
  registration: '📋 Registration',
  waiting: '⏳ Waiting',
  active: '🟢 Active',
  finished: '✅ Finished',
};

const TournamentMonitor = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await tournamentService.getTournamentMonitor(id);
      if (res && res.success) setData(res);
      else setError((res && res.error) || 'Could not load monitor');
    } catch (e) {
      setError(e.message || 'Could not load monitor');
    }
  }, [id]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load]);

  const tournament = data?.tournament;
  const tables = data?.tables || [];
  const players = data?.players || [];

  return (
    <div style={{ padding: '16px', maxWidth: '1000px', margin: '0 auto', color: '#eee', background: '#0d0d0d', minHeight: '100vh' }}>
      <h1>🖥️ Tournament Monitor</h1>
      {error && <div style={{ color: '#f44336', marginBottom: '12px' }}>{error}</div>}

      {tournament && (
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ margin: '0 0 4px' }}>{tournament.name}</h2>
          <div style={{ opacity: 0.8 }}>
            Status: <strong>{STATUS_LABEL[tournament.status] || tournament.status}</strong> · Players: {tournament.currentPlayers}/{tournament.maxPlayers}
            {tournament.tournamentStart ? <> · Starts: {new Date(tournament.tournamentStart).toLocaleString()}</> : null}
          </div>
        </div>
      )}

      <h3>Tables ({tables.length})</h3>
      {tables.length === 0 ? (
        <div style={{ padding: '16px', opacity: 0.6 }}>No tables yet. Start the tournament to create tables.</div>
      ) : (
        tables.map((tbl) => (
          <div key={tbl.gameId} style={{ border: '1px solid #333', borderRadius: '8px', padding: '12px', marginBottom: '12px', background: '#111' }}>
            <div style={{ fontWeight: 700, marginBottom: '8px' }}>
              Table {tbl.tableNumber} <span style={{ opacity: 0.6 }}>(round {tbl.roundNumber})</span> — {STATUS_LABEL[tbl.status] || tbl.status}
              {tbl.winnerId && (
                <span style={{ color: '#4caf50' }}>
                  {' '}· Winner: {(tbl.players || []).find((p) => p.userId === tbl.winnerId)?.username || `#${tbl.winnerId}`}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {(tbl.players || []).map((p) => (
                <span key={p.userId} style={{ padding: '4px 8px', borderRadius: '4px', background: p.userId === tbl.winnerId ? '#1b5e20' : '#222' }}>
                  {p.username}{p.seatPosition ? ` (seat ${p.seatPosition})` : ''}
                </span>
              ))}
            </div>
          </div>
        ))
      )}

      <h3>Players ({players.length})</h3>
      {players.length === 0 ? (
        <div style={{ padding: '16px', opacity: 0.6 }}>No players yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #333' }}>
              <th style={{ padding: '6px' }}>Player</th>
              <th style={{ padding: '6px' }}>Seat</th>
              <th style={{ padding: '6px' }}>Score</th>
              <th style={{ padding: '6px' }}>Round</th>
              <th style={{ padding: '6px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.user_id} style={{ borderBottom: '1px solid #222' }}>
                <td style={{ padding: '6px' }}>{p.username}</td>
                <td style={{ padding: '6px' }}>{p.seat_position || '—'}</td>
                <td style={{ padding: '6px' }}>{p.score || 0}</td>
                <td style={{ padding: '6px' }}>{p.round_number || 1}</td>
                <td style={{ padding: '6px' }}>
                  {p.eliminated ? (
                    <span style={{ color: '#f44336' }}>❌ Eliminated</span>
                  ) : p.final_rank ? (
                    <span style={{ color: '#4caf50' }}>🏆 #{p.final_rank}</span>
                  ) : (
                    'Playing'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '16px', fontSize: '12px', opacity: 0.5 }}>Auto-refreshes every 5 seconds — keep this window open while you play.</div>
    </div>
  );
};

export default TournamentMonitor;
